import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { easing } from "@/lib/animations";

const TABS = [
    { id: "orders", label: "Orders" },
    { id: "addresses", label: "Addresses" },
    { id: "returns", label: "Returns" },
    { id: "settings", label: "Settings" },
];

const MOCK_ORDERS = [
    {
        id: "PRF-A1B2C3",
        date: "2026-01-14",
        total: "€  198,00",
        status: "Delivered",
        items: 2,
    },
    {
        id: "PRF-7K9X4M",
        date: "2025-12-22",
        total: "€  69,00",
        status: "Shipped",
        items: 1,
    },
];

const SignedOutView = ({ onSignIn, mode, setMode }) => {
    const [form, setForm] = useState({ email: "", password: "", name: "" });
    const update = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easing.primary }}
            className="proof-grid-main"
            data-testid="account-signed-out"
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSignIn(form);
                }}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 18,
                }}
                data-testid="account-auth-form"
            >
                <div
                    style={{
                        display: "flex",
                        gap: 0,
                        borderBottom: "1px solid rgba(255,255,255,0.14)",
                        marginBottom: 8,
                    }}
                >
                    {["signin", "signup"].map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setMode(m)}
                            data-testid={`account-tab-${m}`}
                            style={{
                                background: "transparent",
                                border: "none",
                                color: mode === m ? "#fff" : "rgba(255,255,255,0.5)",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                padding: "14px 18px",
                                cursor: "pointer",
                                borderBottom:
                                    mode === m
                                        ? "1px solid #fff"
                                        : "1px solid transparent",
                                marginBottom: -1,
                                transition: "all 0.3s",
                            }}
                        >
                            {m === "signin" ? "Sign In" : "Create Account"}
                        </button>
                    ))}
                </div>

                {mode === "signup" && (
                    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <span
                            style={{
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.55)",
                            }}
                        >
                            Full name
                        </span>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={update}
                            data-testid="account-name"
                            style={fieldStyle}
                        />
                    </label>
                )}

                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span
                        style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.55)",
                        }}
                    >
                        Email
                    </span>
                    <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={update}
                        required
                        data-testid="account-email"
                        style={fieldStyle}
                    />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span
                        style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.55)",
                        }}
                    >
                        Password
                    </span>
                    <input
                        type="password"
                        name="password"
                        value={form.password}
                        onChange={update}
                        required
                        data-testid="account-password"
                        style={fieldStyle}
                    />
                </label>

                <button
                    type="submit"
                    data-testid="account-submit"
                    style={{
                        marginTop: 8,
                        background: "#fff",
                        color: "#0c0c0c",
                        border: "none",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        padding: "14px 0",
                        cursor: "pointer",
                    }}
                >
                    {mode === "signin" ? "Sign In →" : "Create Account →"}
                </button>

                <span
                    style={{
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 13,
                        color: "rgba(255,255,255,0.45)",
                        lineHeight: 1.55,
                    }}
                >
                    Authentication will run via Shopify Customer Accounts in
                    production. UI mocked for preview.
                </span>
            </form>

            <aside
                style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    padding: "28px 26px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                <h3
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700,
                        fontSize: 22,
                        textTransform: "uppercase",
                        letterSpacing: "-0.01em",
                    }}
                >
                    Why an account?
                </h3>
                <ul
                    style={{
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: 16,
                        lineHeight: 1.55,
                        color: "rgba(255,255,255,0.72)",
                    }}
                >
                    <li>
                        — Early access to Field drops, twenty-four hours before
                        the open release.
                    </li>
                    <li>
                        — Returns and exchanges streamlined through Wygodne
                        Zwroty.
                    </li>
                    <li>
                        — One archive for every order. The way your wardrobe
                        thinks.
                    </li>
                </ul>
            </aside>
        </motion.div>
    );
};

const fieldStyle = {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.25)",
    color: "#fff",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 14,
    padding: "12px 14px",
    letterSpacing: "0.02em",
    outline: "none",
};

const SignedInView = ({ session, onSignOut }) => {
    const [tab, setTab] = useState("orders");
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easing.primary }}
            data-testid="account-signed-in"
            className="proof-grid-account"
        >
            <nav
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    borderRight: "1px solid rgba(255,255,255,0.14)",
                    paddingRight: 22,
                }}
                aria-label="Account"
            >
                <div
                    style={{
                        padding: "0 0 14px 0",
                        marginBottom: 8,
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <span
                        style={{
                            display: "block",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "rgba(255,255,255,0.5)",
                            marginBottom: 6,
                        }}
                    >
                        Signed in as
                    </span>
                    <span
                        style={{
                            display: "block",
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: 15,
                            color: "#fff",
                            wordBreak: "break-all",
                        }}
                    >
                        {session.email}
                    </span>
                </div>
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        data-testid={`account-section-${t.id}`}
                        style={{
                            background: "transparent",
                            border: "none",
                            textAlign: "left",
                            color: tab === t.id ? "#fff" : "rgba(255,255,255,0.55)",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            padding: "12px 0",
                            cursor: "pointer",
                            borderLeft:
                                tab === t.id
                                    ? "2px solid #c1121f"
                                    : "2px solid transparent",
                            paddingLeft: 12,
                            transition: "all 0.25s",
                        }}
                    >
                        {t.label}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={onSignOut}
                    data-testid="account-signout"
                    style={{
                        marginTop: 22,
                        background: "transparent",
                        border: "none",
                        color: "rgba(255,255,255,0.45)",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        textAlign: "left",
                        padding: "10px 0 0 12px",
                        cursor: "pointer",
                    }}
                >
                    Sign Out →
                </button>
            </nav>

            <div data-testid={`account-panel-${tab}`}>
                <AnimatePresence mode="wait">
                    {tab === "orders" && (
                        <motion.div
                            key="orders"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.4, ease: easing.primary }}
                            style={{ display: "flex", flexDirection: "column", gap: 16 }}
                        >
                            <h3 style={panelHeading}>Order Archive</h3>
                            {MOCK_ORDERS.map((o) => (
                                <div
                                    key={o.id}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "minmax(120px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) auto",
                                        gap: 16,
                                        padding: "16px 0",
                                        borderBottom:
                                            "1px solid rgba(255,255,255,0.1)",
                                        fontFamily: "JetBrains Mono, monospace",
                                        fontSize: 12,
                                        letterSpacing: "0.16em",
                                        color: "rgba(255,255,255,0.78)",
                                        alignItems: "center",
                                    }}
                                    data-testid={`account-order-${o.id}`}
                                >
                                    <span style={{ color: "#fff" }}>{o.id}</span>
                                    <span>{o.date}</span>
                                    <span>
                                        {o.items} {o.items === 1 ? "item" : "items"}
                                    </span>
                                    <span>{o.total}</span>
                                    <span
                                        style={{
                                            color:
                                                o.status === "Delivered"
                                                    ? "rgba(255,255,255,0.85)"
                                                    : "#c1121f",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.22em",
                                            fontSize: 10,
                                        }}
                                    >
                                        [{o.status}]
                                    </span>
                                </div>
                            ))}
                        </motion.div>
                    )}
                    {tab === "addresses" && (
                        <motion.div
                            key="addresses"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.4, ease: easing.primary }}
                        >
                            <h3 style={panelHeading}>Saved Addresses</h3>
                            <div
                                style={{
                                    padding: "20px 22px",
                                    border:
                                        "1px solid rgba(255,255,255,0.14)",
                                    background: "rgba(255,255,255,0.03)",
                                    fontFamily: "'Space Grotesk', sans-serif",
                                    fontSize: 14,
                                    lineHeight: 1.55,
                                    color: "rgba(255,255,255,0.8)",
                                    maxWidth: 420,
                                }}
                            >
                                <span
                                    style={{
                                        display: "block",
                                        fontFamily: "JetBrains Mono, monospace",
                                        fontSize: 10,
                                        letterSpacing: "0.28em",
                                        textTransform: "uppercase",
                                        color: "rgba(255,255,255,0.45)",
                                        marginBottom: 6,
                                    }}
                                >
                                    Default
                                </span>
                                {session.name || "PROOF Friend"}
                                <br />
                                Ulica Próżna 14/4
                                <br />
                                00-107 Warsaw, Poland
                            </div>
                        </motion.div>
                    )}
                    {tab === "returns" && (
                        <motion.div
                            key="returns"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.4, ease: easing.primary }}
                        >
                            <h3 style={panelHeading}>Returns &amp; Exchanges</h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "'EB Garamond', serif",
                                    fontStyle: "italic",
                                    fontSize: 17,
                                    lineHeight: 1.6,
                                    color: "rgba(255,255,255,0.78)",
                                    maxWidth: "52ch",
                                }}
                            >
                                Every return flows through Wygodne Zwroty —
                                pick a parcel locker, print the label, send the
                                piece back. Refunds are issued within five days
                                of receipt.
                            </p>
                            <Link
                                to="/policy/returns"
                                data-testid="account-returns-policy"
                                style={{
                                    marginTop: 22,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 12,
                                    color: "#fff",
                                    textDecoration: "none",
                                    border: "1px solid rgba(255,255,255,0.45)",
                                    padding: "12px 22px",
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.28em",
                                    textTransform: "uppercase",
                                }}
                            >
                                Read Full Policy
                            </Link>
                        </motion.div>
                    )}
                    {tab === "settings" && (
                        <motion.div
                            key="settings"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.4, ease: easing.primary }}
                        >
                            <h3 style={panelHeading}>Settings</h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "'EB Garamond', serif",
                                    fontStyle: "italic",
                                    fontSize: 17,
                                    lineHeight: 1.6,
                                    color: "rgba(255,255,255,0.7)",
                                    maxWidth: "44ch",
                                }}
                            >
                                Profile, password and communication preferences
                                will live here. To be wired up to the Shopify
                                Customer API.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

const panelHeading = {
    margin: "0 0 20px 0",
    fontFamily: "'Space Grotesk', Manrope, sans-serif",
    fontWeight: 700,
    fontSize: "clamp(22px, 2.4vw, 32px)",
    letterSpacing: "-0.025em",
    textTransform: "uppercase",
    color: "#fff",
};

export default function Account() {
    const [session, setSession] = useState(null);
    const [mode, setMode] = useState("signin");

    const handleSignIn = (form) => {
        // PLACEHOLDER auth — replaced by Shopify Customer in Phase 3
        if (!form.email) return;
        setSession({ email: form.email, name: form.name || "" });
    };

    return (
        <PageShell
            sectionNumber="07"
            eyebrow={session ? "Field Log" : "Welcome back"}
            title={session ? "Account" : "Sign In"}
            crumb="Account"
            data-testid="page-account"
        >
            {session ? (
                <SignedInView session={session} onSignOut={() => setSession(null)} />
            ) : (
                <SignedOutView
                    onSignIn={handleSignIn}
                    mode={mode}
                    setMode={setMode}
                />
            )}
        </PageShell>
    );
}
